import Box from '@mui/material/Box';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import type { ExchangeSegmentPartnerPlatformShare, SegmentPartnerRevenueMethod } from '@openx/types/gqlTypes.ts';
import type { AudiencePricing } from '@openx/types/targeting/audience.ts';
import type { ContextualPricingApi } from '@openx/types/targeting/contextualPricing.ts';
import { useMemo } from 'react';
import { FeeSummarySection } from './FeeSummarySection.tsx';
import { OrganizationPricingItem } from './OrganizationPricingItem.tsx';
import { PartnerFeeItem } from './PartnerFeeItem.tsx';
import { usePartnerFees } from './usePartnerFees.ts';
import { createPricingConfig } from './utils/pricingCalculations.ts';

export type PricingDetailsDisplayProps = Partial<AudiencePricing> & {
  contextualPricing?: ContextualPricingApi | null;
  segmentPartnerRevenueMethod: SegmentPartnerRevenueMethod | null | undefined;
  isAdmin?: boolean;
  segmentPartnerPlatformShare?: Array<ExchangeSegmentPartnerPlatformShare | null> | null;
};

export const PricingDetailsDisplay = ({
  pricing_by_org,
  totals,
  contextualPricing,
  segmentPartnerRevenueMethod,
  isAdmin = false,
  segmentPartnerPlatformShare,
}: PricingDetailsDisplayProps) => {
  const { rows, hasPlatformFee, shouldShowPartnerType, hasFees, feeSummary, organizations, isLoading } = usePartnerFees(
    {
      pricing_by_org,
      totals,
      contextualPricing,
      segmentPartnerRevenueMethod,
    }
  );

  // Create a lookup map for platform fees by partner_id
  const platformFeesByPartner = useMemo(() => {
    if (!segmentPartnerPlatformShare) return {};

    return segmentPartnerPlatformShare.reduce(
      (acc: Record<string, string>, fee: ExchangeSegmentPartnerPlatformShare | null) => {
        if (fee?.partner_id && fee?.share) {
          const percentValue = Number(fee.share) * 100;

          acc[fee.partner_id] = String(percentValue);
        }
        return acc;
      },
      {} as Record<string, string>
    );
  }, [segmentPartnerPlatformShare]);

  // the organizations only name the partners, so the fees show without them (by id) when their fetch fails
  if (!segmentPartnerRevenueMethod || !feeSummary || !hasFees) {
    return null;
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box data-test="pricing-details">
      {rows.map(row => (
        <PartnerFeeItem
          key={`${row.partnerType}-${row.partnerId}`}
          row={row}
          organizationName={organizations[Number(row.partnerId)]?.name}
          shouldShowPartnerType={shouldShowPartnerType}
        >
          <OrganizationPricingItem
            pricing={createPricingConfig(segmentPartnerRevenueMethod, row.priceCpm, row.pricePom)}
            isAdmin={isAdmin}
            isEditMode={false}
            // a row without a platform fee must not show the share its partner has on another row
            {...(hasPlatformFee(row)
              ? { partnerId: row.partnerId, platformFeeValue: platformFeesByPartner[row.partnerId] }
              : {})}
          />
        </PartnerFeeItem>
      ))}
      <FeeSummarySection summary={feeSummary} isLabelledByType={shouldShowPartnerType} />
    </Box>
  );
};
